import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import './AddTemplates.css';
import { v4 as uuidv4 } from 'uuid';
import { hasCircularReference } from '../utils/templateValidation';
import { authFetch, isInteractiveAuthError } from '../authFetch';

function slugify(text) {
  return text
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-') // collapse dashes
    .replace(/^-|-$|/g, '') // trim -
    ;
}

function AddTemplates() {
  const [templateName, setTemplateName] = useState('');
  const [sections, setSections] = useState([]);
  const [generatedJson, setGeneratedJson] = useState(null);
  
  // New state for template editing functionality
  const [mode, setMode] = useState('create'); // 'create' or 'edit'
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showArchived, setShowArchived] = useState(false); // Toggle to show/hide archived templates

  // MSAL
  const { accounts } = useMsal();

  // Fetch templates on mount - get only latest versions for editing dropdown
  useEffect(() => {
    const TEMPLATES_API_ENDPOINT = process.env.REACT_APP_TEMPLATES_API_ENDPOINT ||
      'https://68ysvrjdb7.execute-api.eu-west-2.amazonaws.com/formTemplates-dev';

    const fetchTemplates = async () => {
      try {
        // Fetch latest versions only for the dropdown
        const res = await authFetch(`${TEMPLATES_API_ENDPOINT}?latestOnly=true`);
        if (!res.ok) {
          console.error('Failed to fetch templates:', res.statusText);
          return;
        }
        const data = await res.json();
        // Expecting data to be array, each row may contain template object inside payload/template key
        const normalised = data.map(item => {
          const template = item.template || item.payload?.template || item;
          return {
            ...template,
            version: item.version || template.version || 1,
            archived: item.archived || template.archived || false // Backend returns archived status
          };
        });
        setTemplates(normalised);
        console.log('[AddTemplates] Fetched templates:', normalised.map(t => ({ id: t.id, name: t.name, version: t.version, archived: t.archived })));
      } catch (err) {
        console.error('Error fetching templates:', err);
      }
    };

    fetchTemplates();
  }, []);

  // Load selected template data when editing
  const loadTemplateForEditing = (template) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    
    // Parse template definition back to sections format and preserve IDs
    const parsedSections = [];
    let currentSection = null;

    template.definition.forEach(def => {
      if (def.type === 'section') {
        // Push previous section if exists
        if (currentSection) parsedSections.push(currentSection);
        // Start a new section with preserved id (notes removed)
        currentSection = { id: def.id, title: def.label, fields: [] };
      } else if (currentSection) {
        // Skip legacy automatically appended Section Notes fields
        if (def.type === 'textarea' && def.label === 'Section Notes') return;
        // Preserve field ids and properties
        const field = { id: def.id, label: def.label, type: def.type };
        
        // Preserve template-reference specific properties
        if (def.type === 'template-reference') {
          if (def.templateId) field.templateId = def.templateId;
          if (def.required) field.required = def.required;
        }
        
        // Preserve showInParent property
        if (def.showInParent) {
          field.showInParent = true;
        }
        
        currentSection.fields.push(field);
      }
    });
    // Push the last section if present
    if (currentSection) parsedSections.push(currentSection);

    setSections(parsedSections);
    setGeneratedJson(null);
  };

  // Handle mode change
  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'create') {
      // Reset form for creating new template
      setTemplateName('');
      setSections([]);
      setSelectedTemplateId('');
      setEditingTemplate(null);
      setGeneratedJson(null);
    }
  };

  // Handle template selection for editing
  const handleTemplateSelection = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        loadTemplateForEditing(template);
      }
    }
  };

  const addSection = () => {
    setSections(prev => [
      ...prev,
      { id: uuidv4(), title: '', fields: [] }
    ]);
  };

  const removeSection = (index) => {
    setSections(prev => prev.filter((_, i) => i !== index));
  };

  const updateSectionTitle = (index, value) => {
    setSections(prev => prev.map((s, i) => i === index ? { ...s, title: value } : s));
  };

  const addField = (sectionIdx) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      return {
        ...s,
  fields: [...s.fields, { id: uuidv4(), label: '', type: 'text', templateId: '', required: false }]
      };
    }));
  };

  const updateField = (sectionIdx, fieldIdx, key, value) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      return {
        ...s,
        fields: s.fields.map((f, j) => j === fieldIdx ? { ...f, [key]: value } : f)
      };
    }));
  };

  const removeField = (sectionIdx, fieldIdx) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      return {
        ...s,
        fields: s.fields.filter((_, j) => j !== fieldIdx)
      };
    }));
  };

  // Reorder sections up/down
  const moveSection = (index, direction) => {
    setSections(prev => {
      const target = index + direction;
      if (target < 0 || target >= prev.length) return prev; // no change
      const copy = [...prev];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      return copy;
    });
  };

  // Reorder fields within a section
  const moveField = (sectionIdx, fieldIdx, direction) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== sectionIdx) return s;
      const target = fieldIdx + direction;
      if (target < 0 || target >= s.fields.length) return s; // no change
      const newFields = [...s.fields];
      [newFields[fieldIdx], newFields[target]] = [newFields[target], newFields[fieldIdx]];
      return { ...s, fields: newFields };
    }));
  };

  // Archive a template (updates the latest version's archived flag)
  const archiveTemplate = async (templateId, currentArchivedStatus) => {
    const newArchivedStatus = !currentArchivedStatus;
    const confirmMessage = newArchivedStatus 
      ? 'Are you sure you want to archive this template? It will be hidden from dropdowns and the template list.' 
      : 'Are you sure you want to unarchive this template?';
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      const TEMPLATES_API_ENDPOINT = process.env.REACT_APP_TEMPLATES_API_ENDPOINT ||
        'https://68ysvrjdb7.execute-api.eu-west-2.amazonaws.com/formTemplates-dev';
      
      // Find the template
      const template = templates.find(t => t.id === templateId);
      if (!template) {
        throw new Error('Template not found');
      }
      
      // Create updated template (this will create a new version with the archived flag)
      const updatedTemplate = {
        ...template,
        archived: newArchivedStatus,
        updatedAt: new Date().toISOString(),
        updatedBy: {
          username: accounts[0]?.username || 'unknown_user',
          name: accounts[0]?.name || ''
        }
      };
      
      const userId    = accounts[0]?.localAccountId || accounts[0]?.username || 'unknown_user';
      const userName  = accounts[0]?.name || '';
      const userEmail = accounts[0]?.username || '';
      
      // Submit as an update (will create new version with archived flag)
      const submissionPayload = {
        userId,
        userName,
        userEmail,
        template: updatedTemplate,
        isUpdate: true,
        originalTemplateId: templateId
      };
      
      console.log('[AddTemplates] Archiving template:', {
        templateId,
        newArchivedStatus,
        payload: submissionPayload
      });
      
      // Use the SUBMIT endpoint, not the READ endpoint
      const SUBMIT_TEMPLATE_API_ENDPOINT = 'https://hxppuzt0z1.execute-api.eu-west-2.amazonaws.com/submitFormTemplate-dev';
      
      const response = await authFetch(SUBMIT_TEMPLATE_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionPayload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[AddTemplates] Archive failed:', errorText);
        throw new Error('Failed to update template archived status');
      }
      
      const result = await response.json();
      console.log('[AddTemplates] Archive successful:', result);
      
      // Refetch templates to update the list
      try {
        const res = await authFetch(`${TEMPLATES_API_ENDPOINT}?latestOnly=true`);
        if (res.ok) {
          const data = await res.json();
          const normalised = data.map(item => {
            const template = item.template || item.payload?.template || item;
            return {
              ...template,
              version: item.version || template.version || 1,
              archived: item.archived || template.archived || false // Backend returns archived status
            };
          });
          setTemplates(normalised);
          console.log('[AddTemplates] Refetched templates after archive:', normalised.map(t => ({ id: t.id, name: t.name, archived: t.archived })));
        }
      } catch (err) {
        console.error('Error refetching templates after archive:', err);
      }
      
      alert(newArchivedStatus ? 'Template archived successfully' : 'Template unarchived successfully');
    } catch (error) {
      if (isInteractiveAuthError(error)) return;
      console.error('Error archiving template:', error);
      alert(`Failed to ${newArchivedStatus ? 'archive' : 'unarchive'} template: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!templateName.trim()) {
      alert('Please enter a template name');
      return;
    }

    if (sections.length === 0) {
      alert('Please add at least one section');
      return;
    }

    if (mode === 'edit' && !editingTemplate) {
      alert('No template selected for editing');
      return;
    }

    // Validate template references for circular dependencies
    const currentTemplateId = mode === 'edit' && editingTemplate ? editingTemplate.id : slugify(templateName);
    const templateRefs = [];
    sections.forEach(section => {
      section.fields.forEach(field => {
        if (field.type === 'template-reference' && field.templateId) {
          if (hasCircularReference(currentTemplateId, field.templateId, templates)) {
            templateRefs.push(field.label || 'Unnamed field');
          }
        }
      });
    });
    
    if (templateRefs.length > 0) {
      alert(`Circular reference detected in fields: ${templateRefs.join(', ')}. A template cannot reference itself directly or indirectly.`);
      return;
    }

    const definition = [];
    sections.forEach(section => {
      const sectionId = section.id || uuidv4();
      definition.push({ id: sectionId, type: 'section', label: section.title });
      section.fields.forEach(field => {
        const fieldId = field.id || uuidv4();
        const fieldDef = { id: fieldId, type: field.type, label: field.label };
        
        // Add template-specific fields
        if (field.type === 'template-reference') {
          fieldDef.templateId = field.templateId || '';
          fieldDef.required = field.required || false;
        }
        
        // Add showInParent property for preview fields
        if (field.showInParent) {
          fieldDef.showInParent = true;
        }
        
        definition.push(fieldDef);
      });
    });

    const creatorUsername = accounts[0]?.username || 'unknown_user';
    const creatorName = accounts[0]?.name || '';

    const result = {
      id: mode === 'edit' && editingTemplate ? editingTemplate.id : slugify(templateName),
      name: templateName,
      definition,
      createdAt: mode === 'edit' && editingTemplate ? editingTemplate.createdAt : new Date().toISOString(),
      updatedAt: mode === 'edit' ? new Date().toISOString() : undefined,
      createdBy: mode === 'edit' && editingTemplate ? editingTemplate.createdBy : {
        username: creatorUsername,
        name: creatorName,
      },
      updatedBy: mode === 'edit' ? {
        username: creatorUsername,
        name: creatorName,
      } : undefined,
      archived: mode === 'edit' && editingTemplate ? editingTemplate.archived : false, // Preserve archived status
    };

    console.log('Generated template result:', result);
    console.log('Mode:', mode, 'Editing Template ID:', editingTemplate?.id);
    setGeneratedJson(result);

    // Prepare submission payload for backend Lambda
    const userId    = accounts[0]?.localAccountId || accounts[0]?.username || 'unknown_user';
    const userName  = accounts[0]?.name || '';
    const userEmail = accounts[0]?.username || '';

    const submissionPayload = {
      userId,
      userName,
      userEmail,
      template: result,
      isUpdate: mode === 'edit',
      originalTemplateId: mode === 'edit' && editingTemplate ? editingTemplate.id : undefined,
      operation: mode === 'edit' ? 'UPDATE' : 'CREATE',
      // Add more explicit fields for the backend to understand this is an update
      ...(mode === 'edit' && {
        updateExisting: true,
        existingTemplateId: editingTemplate?.id,
        replaceTemplate: true,
      }),
    };

    const SUBMIT_TEMPLATE_API_ENDPOINT = 'https://hxppuzt0z1.execute-api.eu-west-2.amazonaws.com/submitFormTemplate-dev';

    console.log('Submitting template:', {
      mode,
      templateName,
      sections: sections.length,
      isUpdate: mode === 'edit',
      templateId: mode === 'edit' ? editingTemplate?.id : 'new',
      originalTemplate: editingTemplate ? { id: editingTemplate.id, name: editingTemplate.name } : null,
      endpoint: SUBMIT_TEMPLATE_API_ENDPOINT
    });
    console.log('Full payload:', submissionPayload);

    try {
      // Use the original endpoint for both create and update
      const apiUrl = SUBMIT_TEMPLATE_API_ENDPOINT;
      
      console.log('Final API URL:', apiUrl);

      const response = await authFetch(apiUrl, {
        method: 'POST', // Use POST for both create and update
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionPayload),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      const apiResult = await response.json();
      console.log('API Result:', apiResult);

      if (!response.ok) {
        console.error('API error:', apiResult);
        console.error('Response status:', response.status);
        console.error('Response statusText:', response.statusText);
        alert(`Error ${mode === 'edit' ? 'updating' : 'submitting'} template: ${apiResult.error || response.statusText}`);
        return;
      }

      console.log(`Template ${mode === 'edit' ? 'updated' : 'saved'}:`, apiResult);
      alert(`Template ${mode === 'edit' ? 'updated' : 'submitted'} successfully (ID: ${apiResult.templateId || 'unknown'})`);

      // Reset form to initial state and refetch templates
      setTemplateName('');
      setSections([]);
      setGeneratedJson(null);
      setSelectedTemplateId('');
      setEditingTemplate(null);
      
      // Refetch templates to get updated list
      const TEMPLATES_API_ENDPOINT = process.env.REACT_APP_TEMPLATES_API_ENDPOINT ||
        'https://68ysvrjdb7.execute-api.eu-west-2.amazonaws.com/formTemplates-dev';
      
      try {
        const res = await authFetch(TEMPLATES_API_ENDPOINT);
        if (res.ok) {
          const data = await res.json();
          const normalised = data.map(item => item.template || item.payload?.template || item);
          setTemplates(normalised);
        }
      } catch (err) {
        console.error('Error refetching templates:', err);
      }

    } catch (err) {
      if (isInteractiveAuthError(err)) return;
      console.error(`Network error ${mode === 'edit' ? 'updating' : 'submitting'} template:`, err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      
      // More specific error messages
      let errorMessage = err.message;
      if (err.message === 'Failed to fetch') {
        errorMessage = 'Network error - please check your internet connection and that the API endpoint is accessible';
      }
      
      alert(`Error ${mode === 'edit' ? 'updating' : 'submitting'} template: ${errorMessage}`);
    }
  };

  return (
    <div className="add-templates-container" style={{ padding: '20px' }}>
      {/* Mode Selection */}
  <div style={{ marginBottom: '20px', padding: '15px', border: '2px solid #ddd', borderRadius: 0, backgroundColor: '#f9f9f9' }}>
        <h3>Templating</h3>
        <div style={{ display: 'flex', gap: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="mode"
              value="create"
              checked={mode === 'create'}
              onChange={(e) => handleModeChange(e.target.value)}
              style={{ marginRight: '5px' }}
            />
            Create New Template
          </label>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              name="mode"
              value="edit"
              checked={mode === 'edit'}
              onChange={(e) => handleModeChange(e.target.value)}
              style={{ marginRight: '5px' }}
            />
            Edit Existing Template
          </label>
        </div>
      </div>

      {/* Template Selection for Edit Mode */}
      {mode === 'edit' && (
        <div style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ccc', borderRadius: 0, backgroundColor: '#f5f5f5' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
            <label>
              Select Template to Edit:&nbsp;
              <select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelection(e.target.value)}
                style={{ padding: '5px', minWidth: '200px' }}
              >
                <option value="">-- Select a Template --</option>
                {templates.filter(t => showArchived || !t.archived).map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} (v{template.version || 1}){template.archived ? ' [ARCHIVED]' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show Archived
            </label>
            {editingTemplate && (
              <button
                type="button"
                onClick={() => archiveTemplate(editingTemplate.id, editingTemplate.archived)}
                style={{ 
                  padding: '8px 16px', 
                  background: '#000',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {editingTemplate.archived ? 'Unarchive' : 'Archive'}
              </button>
            )}
          </div>
          {editingTemplate && (
            <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
              Editing will create version {(editingTemplate.version || 1) + 1} of this template.
            </div>
          )}
        </div>
      )}

      {/* Only show form if in create mode OR in edit mode with a selected template */}
      {(mode === 'create' || (mode === 'edit' && selectedTemplateId)) && (
        <form onSubmit={handleSubmit} className="template-form-wrapper">
          <div style={{ marginBottom: '20px' }}>
            <label>
              Template Name:&nbsp;
              <input
                type="text"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                required
              />
            </label>
          </div>

        {sections.map((section, sIdx) => (
          <div key={sIdx} style={{ border: '1px solid #ccc', padding: '8px 15px 15px', marginBottom: '20px', borderRadius: 0 }}>
            <div className="section-header">
              <h3>Section {sIdx + 1}</h3>
              <div className="section-controls">
                <button
                  type="button"
                  className="move-section-btn"
                  onClick={() => moveSection(sIdx, -1)}
                  disabled={sIdx === 0}
                  aria-label="Move section up"
                  title="Move section up"
                >↑</button>
                <button
                  type="button"
                  className="move-section-btn"
                  onClick={() => moveSection(sIdx, 1)}
                  disabled={sIdx === sections.length - 1}
                  aria-label="Move section down"
                  title="Move section down"
                >↓</button>
                <button
                  type="button"
                  className="remove-section-btn"
                  onClick={() => removeSection(sIdx)}
                >Remove Section</button>
              </div>
            </div>
            <div className="section-title-container">
              <label>
                Section Title:&nbsp;
                <input
                  type="text"
                  value={section.title}
                  onChange={e => updateSectionTitle(sIdx, e.target.value)}
                  required
                />
              </label>
            </div>
            {/* Fields */}
            {section.fields.map((field, fIdx) => (
              <div key={fIdx} className="field-container">
                <hr className="field-divider" />
                <div className="field-label-row">
                  <label className="inline-label">Field:</label>
                  <input
                    type="text"
                    value={field.label}
                    onChange={e => updateField(sIdx, fIdx, 'label', e.target.value)}
                    required
                  />
                  <div className="field-move-controls">
                    <button
                      type="button"
                      className="move-field-btn"
                      onClick={() => moveField(sIdx, fIdx, -1)}
                      disabled={fIdx === 0}
                      aria-label="Move field up"
                      title="Move field up"
                    >↑</button>
                    <button
                      type="button"
                      className="move-field-btn"
                      onClick={() => moveField(sIdx, fIdx, 1)}
                      disabled={fIdx === section.fields.length - 1}
                      aria-label="Move field down"
                      title="Move field down"
                    >↓</button>
                  </div>
                </div>
                <div className="field-type-row">
                  <label className="inline-label">Type:</label>
                  <select
                    value={field.type}
                    onChange={e => updateField(sIdx, fIdx, 'type', e.target.value)}
                  >
                    <option value="text">Text</option>
                    <option value="textarea">Textarea</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="date">Date</option>
                    <option value="description">Description (Info Text)</option>
                    <option value="template-reference">Sub-Template</option>
                  </select>
                  <button
                    type="button"
                    className="remove-field-btn"
                    onClick={() => removeField(sIdx, fIdx)}
                  >
                    Remove Field
                  </button>
                </div>
                {/* Show in Parent Preview checkbox for regular fields */}
                {field.type !== 'template-reference' && field.type !== 'section' && field.type !== 'description' && (
                  <div className="field-options-row" style={{ marginTop: '8px', paddingLeft: '12px' }}>
                    <label className="inline-label checkbox-label">
                      <input
                        type="checkbox"
                        checked={field.showInParent || false}
                        onChange={e => updateField(sIdx, fIdx, 'showInParent', e.target.checked)}
                      />
                      Show in parent preview (for nested templates)
                    </label>
                  </div>
                )}
                {/* Template Reference Fields */}
                {field.type === 'template-reference' && (
                  <div className="template-reference-config">
                    <label className="inline-label">Select Template:</label>
                    <select
                      value={field.templateId || ''}
                      onChange={e => updateField(sIdx, fIdx, 'templateId', e.target.value)}
                      required
                    >
                      <option value="">-- Select a Template --</option>
                      {templates.filter(t => !t.archived).map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} (v{template.version || 1})
                        </option>
                      ))}
                    </select>
                    <label className="inline-label checkbox-label">
                      <input
                        type="checkbox"
                        checked={field.required || false}
                        onChange={e => updateField(sIdx, fIdx, 'required', e.target.checked)}
                      />
                      Required
                    </label>
                  </div>
                )}
              </div>
            ))}
            <hr className="field-divider" />
            <button
              type="button"
              className="add-field-btn"
              onClick={() => addField(sIdx)}
            >
              Add Field
            </button>
          </div>
        ))}

        {sections.length > 0 && <hr className="section-list-divider" />}

        <button type="button" onClick={addSection}>Add Section</button>

        <hr className="action-divider" />

          <div style={{ marginTop: '20px' }}>
            <button type="submit">{mode === 'edit' ? 'Update Template' : 'Submit Template'}</button>
          </div>
        </form>
      )}

      {generatedJson && (
        <div style={{ marginTop: '30px' }}>
          <h2>Generated Template JSON</h2>
          <pre style={{ background: '#f4f4f4', padding: '15px' }}>
            {JSON.stringify(generatedJson, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default AddTemplates;
